import { acceptHMRUpdate, defineStore } from 'pinia'
import type {
  SurveyKey,
  SurveyResult,
  UpdateSurveyParams,
  WebsiteSurvey
} from '~/types/surveys'
import kebabcaseKeys from 'kebabcase-keys'
import statusMessages from '/@lang/status-messages.json'
import type { ApiResponse } from '#shared/types/utils'
import { usePersist } from '~/plugins/pinia.ts'

interface State {
  surveys: Record<SurveyKey, SurveyResult | undefined>
}

const userStatusMessages = statusMessages['user-status-messages']

export const useSurveyStore = defineStore('survey', {
  state: (): State => ({
    surveys: {
      'survey-january-2024': undefined,
      'survey-february-2024-cook': undefined
    }
  }),
  persist: usePersist<State>({
    pick: ['surveys']
  }),
  actions: {
    reset() {
      this.$reset()
    },

    async fetch(key: SurveyKey): Promise<boolean> {
      try {
        const result = await this.api.get<ApiResponse<WebsiteSurvey>>(`survey/${key}`)

        if (result.data.answers) {
          this.surveys[key] = kebabcaseKeys(result.data.answers as any, {
            deep: true
          }) as unknown as SurveyResult
        }

        return true
      } catch {
        return false
      }
    },

    async update(key: SurveyKey, isFinished: boolean): Promise<boolean> {
      const updatedSurvey = this.surveys[key]

      if (!updatedSurvey) {
        return false
      }

      const surveyParams = {
        surveyKey: key,
        answers: JSON.stringify(updatedSurvey),
        finished: isFinished
      } satisfies UpdateSurveyParams

      try {
        await this.api.put<ApiResponse<SurveyResult>>('survey/update', surveyParams)
        return true
      } catch (error: any) {
        addToast().error(userStatusMessages[this.currentLocale].error['update-survey'])

        sendSentryMessage('Error while updating survey', 'survey-update', {
          message: error.message
        })

        return false
      }
    }
  },
  getters: {}
})

/**
 * Pinia supports Hot Module replacement, so you can edit your stores and
 * interact with them directly in your app without reloading the page.
 *
 * @see https://pinia.esm.dev/cookbook/hot-module-replacement.html
 * @see https://vitejs.dev/guide/api-hmr.html
 */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSurveyStore, import.meta.hot))
}
